import {Apps} from "enums";
import {BucketInfo, MetaTagGroup} from "interfaces";
import {toCount, toDate} from "./helpers";
import {CommonSearchPayload} from "./interfaces/types";
import {ImagePayloadBloom, ImagePayloadInstinct} from "./interfaces/imagePayloadInstinct";
import {PhotoPayloadFlava, PhotoPayloadInstinct} from "./interfaces/photoPayloadInstinct";


// Single utility function for common field mappings
export const mapCommonFields = (user: any, app: Apps) => ({
    // Count fields
    displaysCount: toCount(user.displaysCount),
    likesCount: toCount(user.likesCount),
    chatsCount: toCount(user.chatsCount),
    outgoingDislikesCount: toCount(user.outgoingDislikesCount),
    outgoingLikesCount: toCount(user.outgoingLikesCount),
    matchesCount: toCount(user.matchesCount),

    // Location fields
    latitude: user.latitude,
    longitude: user.longitude,
    countryCode: user.countryCode,
    ipCountryCode: user.ipCountryCode,

    // Status fields
    status: user.status,
    hasPremium: user.hasPremium,
    isBanned: user.isBanned,
    isPaused: user.isPaused,
    isVerified: user.isVerified,
    reportsCount: user.reportsCount,
    scamMessages: user.scamMessages,

    // Date fields
    verificationType: user.verificationType,
    startDate: user.startDate ? toDate(user.startDate, app) : null,
    previousEndDate: user.previousEndDate ? toDate(user.previousEndDate, app) : null,
    temporaryUnbannedDate: user.temporaryUnbannedDate ? toDate(user.temporaryUnbannedDate, app) : undefined,
    city: user.city ?? user.cityName,
    registrationDate: user.registrationDate ? toDate(user.registrationDate, app) : undefined,
    premiumStartDate: user.premiumActivationDate ? toDate(user.premiumActivationDate, app) : undefined,
    renewCount: user.premiumRenewCount,
    sessionNumber: user.sessionNumber,
    isPushNotificationEnabled: user.isPushNotificationEnabled,
});

// Helper function to group meta tags by groupId
export const groupMetaTags = (metaTags: CommonSearchPayload['metaTags'], app: Apps): MetaTagGroup[] => {
    if (!metaTags) return [];

    return metaTags.reduce((groups, currentTag) => {
        const existingGroup = groups.find(group => group.groupId === currentTag.groupId);
        const convertedTag = {...currentTag, tag: app === Apps.Flava ? currentTag.text : currentTag.tag};

        if (existingGroup) {
            existingGroup.interests.push(convertedTag);
        } else {
            groups.push({groupId: currentTag.groupId, interests: [convertedTag]});
        }

        return groups;
    }, [] as MetaTagGroup[]);
};

// Helper function to map bucket data
export const mapBucketData = (buckets: Array<BucketInfo<string>> | undefined, app: Apps) => {
    return buckets?.map(bucket => ({
        ...bucket,
        date: toDate(bucket.date, app),
        expiresDate: toDate(bucket.expiresDate, app),
        usageTimes: bucket.usageTimes
            .map(ut => toDate(ut, app))
            .filter(ut => ut) as Date[]
    })) ?? [];
};

// Photo conversion utilities
export const convertImageToBloom = (image: ImagePayloadInstinct): ImagePayloadBloom => ({
    ...image,
    link: image.url
});

export const convertPhotoToBloom = (photo: PhotoPayloadInstinct) => ({
    ...photo,
    isPrivate: false,
    image: photo.image?.map(convertImageToBloom) || []
});

// Helper function to convert Flava photos to Bloom format
export const convertFlavaPhotoToBloom = (photo: PhotoPayloadFlava) => ({
    ...photo,
    key: photo.id,
    isPrivate: false,
    image: photo.image.map(i => ({...i, link: i.url} as ImagePayloadBloom)),
});
